import { Client as BelfastClient } from '@kaizenplatform/belfast';
import { FileReference } from '@kaizenplatform/belfast-entity';
import { ModificationRequestItemMetadata } from '@kaizenplatform/hugot-entity';
import { History } from 'history';
import { parse } from 'qs';
import Store from 'repatch';

import State from '../core/State';
import CreateIssue from '../entity/CreateIssue';
import CreateIssueComment from '../entity/CreateIssueComment';
import EditModificationRequestItem from '../entity/EditModificationRequestItem';
import ModificationRequestItemInput from '../entity/ModificationRequestItemsInput';
import UploadingFile from '../entity/UploadingFile';
import { ModificationRequestFeeType } from '../generated/graphql';
import {
  getLatestDeliverable,
  getLatestModificationRequest,
  getModificationRequestsWithoutDraft,
} from '../graphql/creation';
import ErrorPresenter from '../service/ErrorPresenter';
import MessageDispatcher from '../service/MessageDispatcher';
import CreationWebApi from '../service/persistent/CreationWebApi';
import FilesWebApi from '../service/persistent/FilesWebApi';
import ModificationRequestWebApi, {
  AlreadyApprovedModificationRequestError,
  AlreadyModificationRequestedError,
  ExpiredModificationRequestError,
} from '../service/persistent/ModificationRequestWebApi';
import { setCreation } from '../thunk/creation';
import {
  decrement as decrementFullScreenSpinnerNecessity,
  increment as incrementFullScreenSpinnerNecessity,
} from '../thunk/fullScreenSpinner';
import { setLastAccessedOrganization } from '../thunk/lastAccessedOrganization';
import {
  addAssetFile,
  addAssetIssueCommentFile,
  addAssetIssueFile,
  addEditAssetFile,
  addModificationRequestItem,
  addModificationRequestToEditing,
  addUploadedEditAssetFiles,
  addUploadingAssetFiles,
  addUploadingEditAssetFiles,
  addUploadingIssueAssetFiles,
  addUploadingIssueCommentAssetFiles,
  dispose,
  disposeEditing,
  disposeEditModificationRequestItem,
  finishFetchingAssetFiles,
  finishFetchingModificationRequest,
  removeAllAssetFile,
  removeAssetFile,
  removeAssetIssueCommentFile,
  removeAssetIssueFile,
  removeEditAssetFile,
  removeUploadingAssetFile,
  removeUploadingEditAssetFile,
  removeUploadingEditFilesProgress,
  removeUploadingFilesProgress,
  removeUploadingIssueAssetFile,
  removeUploadingIssueCommentAssetFile,
  removeUploadingIssueCommentFilesProgress,
  removeUploadingIssueFilesProgress,
  resetUploadingIssueAssetFile,
  resetUploadingIssueCommentAssetFile,
  setAllModificationRequests,
  setCreateIssue,
  setCreateIssueComment,
  setEditModificationRequestItem,
  setModificationRequest,
  setModificationRequestIssue,
  setModificationRequestIssueComment,
  setModificationRequestItems,
  setReopenModificationRequestIssue,
  setResolveModificationRequestIssue,
  setSelectedDeliverable,
  setSelectedFeeType,
  startFetchingModificationRequest,
  updateModificationRequestItem,
  updateUploadingEditFilesProgress,
  updateUploadingFilesProgress,
  updateUploadingIssueCommentFilesProgress,
  updateUploadingIssueFilesProgress,
} from '../thunk/modificationRequest';
import validateUploadingFiles from '../utility/validateUploadingFiles';

class ModificationRequestUsecase {
  private readonly store: Store<State>;
  private readonly history: History;
  private readonly belfastClient: BelfastClient;
  private readonly messageDispatcher: MessageDispatcher;
  private readonly errorPresenter: ErrorPresenter;
  private readonly creationWebApi: CreationWebApi;
  private readonly modificationRequestWebApi: ModificationRequestWebApi;
  private readonly filesWebApi: FilesWebApi;

  constructor({
    store,
    history,
    belfastClient,
    messageDispatcher,
    errorPresenter,
    creationWebApi,
    modificationRequestWebApi,
    filesWebApi,
  }: {
    store: Store<State>;
    history: History;
    belfastClient: BelfastClient;
    messageDispatcher: MessageDispatcher;
    errorPresenter: ErrorPresenter;
    creationWebApi: CreationWebApi;
    modificationRequestWebApi: ModificationRequestWebApi;
    filesWebApi: FilesWebApi;
  }) {
    this.store = store;
    this.history = history;
    this.belfastClient = belfastClient;
    this.messageDispatcher = messageDispatcher;
    this.errorPresenter = errorPresenter;
    this.creationWebApi = creationWebApi;
    this.modificationRequestWebApi = modificationRequestWebApi;
    this.filesWebApi = filesWebApi;
  }

  public prepare = async (): Promise<void> => {
    this.store.dispatch(startFetchingModificationRequest());

    const creation = this.store.getState().creation.creation;
    if (!creation) {
      throw new Error('Creation not found.');
    }

    const latestDeliverable = getLatestDeliverable(creation);
    if (!latestDeliverable) {
      throw new Error('Deliverable not found.');
    }

    const modificationRequestsWithoutDraft = getModificationRequestsWithoutDraft(creation);

    // Use In-Page Link only.
    const { deliverable: deliverableId } = parse(this.history.location.search, { ignoreQueryPrefix: true });
    const selectedDeliverable = creation.deliverables.find((deliverable) => deliverable.id === deliverableId);

    //* 1. Set ModificationRequests
    this.store.dispatch(setAllModificationRequests(modificationRequestsWithoutDraft));

    //* 2. Check Query Parameter and Dispatch Selected DeliverableId
    if (deliverableId && selectedDeliverable) {
      this.store.dispatch(setSelectedDeliverable(selectedDeliverable.id));
    }

    //* 3. Get all assets files
    const fileItems: FileReference[] = [];

    Promise.all(
      creation.modificationRequests.map(async (modificationRequest) => {
        if (modificationRequest.bucketId) {
          const token = await this.filesWebApi.fetchToken({
            type: 'bucket',
            bucketType: 'modification_request',
            bucketId: modificationRequest.bucketId,
          });

          await this.belfastClient.getAllFilesInBucket(modificationRequest.bucketId, token).then((files) => {
            this.store.dispatch(finishFetchingAssetFiles(files));
          });
        }
      }),
    ).then(() => this.store.dispatch(finishFetchingAssetFiles(fileItems)));

    //* 4. Set ModificationRequestIssueComment
    const createIssueComments: Array<CreateIssueComment> = [];
    modificationRequestsWithoutDraft.map((modificationRequest) =>
      modificationRequest.items.map((item) => {
        if (item.issue) {
          createIssueComments.push({
            issueId: item.issue.id,
            assetIds: [],
            uploadingAssetFiles: [],
            uploadingFilesProgress: {},
          });
        }
      }),
    );
    this.store.dispatch(setCreateIssueComment(createIssueComments));

    //* 5. Set ModificationRequestIssue
    const createIssues: Array<CreateIssue> = [];
    modificationRequestsWithoutDraft.map((modificationRequest) =>
      modificationRequest.items.map((item) => {
        if (item.id) {
          createIssues.push({
            itemId: item.id,
            bucketId: item.bucketId || null,
            assetIds: [],
            uploadingAssetFiles: [],
            uploadingFilesProgress: {},
          });
        }
      }),
    );

    this.store.dispatch(setCreateIssue(createIssues));
    this.store.dispatch(finishFetchingModificationRequest());
  };

  public createModificationRequestAsDraft = async () => {
    const state = this.store.getState();
    const creation = state.creation.creation!;
    const latestDeliverable = getLatestDeliverable(creation)!;

    const latestModificationRequest =
      creation && creation.modificationRequests.length > 0 ? getLatestModificationRequest(creation) : null;

    const bucketId = state.modificationRequestEditing.createModificationRequest.bucketId;

    //* Check Latest ModificationRequest State
    if (!creation.modificationRequestExpired) {
      // draft の修正依頼が存在するなら使う
      if (latestModificationRequest && latestModificationRequest.state === 'draft') {
        this.store.dispatch(addModificationRequestToEditing(latestModificationRequest));
      }

      // 修正依頼を未作成 or 修正依頼差し戻しの場合のみ draft 状態の修正依頼を作成する
      // S3 の backet を作成するため
      if (!latestModificationRequest || latestModificationRequest.state === 'given_feedback') {
        if (!bucketId) {
          try {
            const newModificationRequest = await this.modificationRequestWebApi.createModificationRequestAsDraft(
              latestDeliverable.id,
            );

            this.store.dispatch(addModificationRequestToEditing(newModificationRequest));
          } catch (err) {
            if (err instanceof AlreadyModificationRequestedError) {
              this.errorPresenter.showAsNonFatal(new Error('message.already_modification_requested'));
            } else if (err instanceof ExpiredModificationRequestError) {
              this.errorPresenter.showAsNonFatal(new Error('message.expired_modification_requested'));
            }
            throw err;
          }
        }
      }
    }
  };

  public cancelCreatingDraftModificationRequest = async () => {
    const state = this.store.getState();
    const createModificationRequest = state.modificationRequestEditing.createModificationRequest;
    if (createModificationRequest.id === '') {
      return;
    }

    try {
      const creation = await this.modificationRequestWebApi.cancelCreatingDraftModificationRequest(
        createModificationRequest.id,
      );

      this.store.dispatch(disposeEditing());

      const { items, bucketId } = state.modificationRequestEditing.createModificationRequest;

      if (items.length > 0) {
        items.forEach((item) =>
          item.assetIds.forEach(async (fileId) => await this.belfastClient.deleteFile(fileId, bucketId)),
        );
      }

      this.store.dispatch(setCreation(creation));
    } catch (err) {
      this.errorPresenter.showAsNonFatal(new Error('message.modification_request_sent_failed'));
      throw err;
    }
  };

  public uploadAssetFiles = async (files: UploadingFile[]): Promise<void> => {
    const bucketId = this.store.getState().modificationRequestEditing.createModificationRequest.bucketId;

    const state = this.store.getState();
    if (state.lastAccessedOrganization == null) {
      return;
    }
    const organizationId = state.lastAccessedOrganization.id;

    let uploadingFiles;

    try {
      uploadingFiles = validateUploadingFiles(files);

      if (uploadingFiles) {
        this.store.dispatch(addUploadingAssetFiles(uploadingFiles));

        for (const file of uploadingFiles) {
          await this.belfastClient
            .uploadFile(file, bucketId, organizationId, {
              onUploadProgress: (progressEvent) => {
                const progress = Math.floor((progressEvent.loaded / progressEvent.total) * 100);
                this.store.dispatch(updateUploadingFilesProgress(file.id, progress));
              },
            })
            .then((fileReference) => {
              const {
                uploadingAssetFiles,
              } = this.store.getState().modificationRequestEditing.createModificationRequest;

              this.store.dispatch(removeUploadingFilesProgress(file.id));
              this.store.dispatch(removeUploadingAssetFile(file));

              // TODO: キャンセルされたファイルだった場合は反映しないようにしているが本来は belfast に Cancel API があってほしい
              if (uploadingAssetFiles.filter((uploadingFile) => uploadingFile.id === file.id).length > 0) {
                this.store.dispatch(addAssetFile(fileReference));
              }
            });
        }
      }
    } catch (err) {
      this.errorPresenter.showAsNonFatal(err);
      throw err;
    }
  };

  public removeAssetFile = async (bucketId: string, fileId: string) => {
    await this.belfastClient.deleteFile(fileId, bucketId);
    this.store.dispatch(removeAssetFile(fileId));
  };

  public removeAllAssetFile = () => {
    const { bucketId, assetIds } = this.store.getState().modificationRequestEditing.createModificationRequest;
    this.store.dispatch(removeAllAssetFile());

    if (assetIds.length > 0) {
      assetIds.forEach(async (fileId) => await this.belfastClient.deleteFile(fileId, bucketId));
    }
  };

  public uploadEditAssetFiles = async (files: UploadingFile[], bucketId: string): Promise<void> => {
    let uploadingFiles;
    const state = this.store.getState();
    const editModificationRequestItem = state.modificationRequestEditing.editModificationRequestItem;
    if (state.lastAccessedOrganization == null || editModificationRequestItem == null) {
      return;
    }
    const organizationId = state.lastAccessedOrganization.id;

    try {
      uploadingFiles = validateUploadingFiles(files);

      if (uploadingFiles) {
        this.store.dispatch(addUploadingEditAssetFiles(uploadingFiles));

        for (const file of uploadingFiles) {
          await this.belfastClient
            .uploadFile(file, bucketId, organizationId, {
              onUploadProgress: (progressEvent) => {
                const progress = Math.floor((progressEvent.loaded / progressEvent.total) * 100);
                this.store.dispatch(updateUploadingEditFilesProgress(file.id, progress));
              },
            })
            .then((fileReference) => {
              if (editModificationRequestItem.item != null) {
                this.modificationRequestWebApi
                  .addModificationRequestItemAsset(editModificationRequestItem.item.id, fileReference.id)
                  .then(() => {
                    this.store.dispatch(addUploadedEditAssetFiles(fileReference.id));
                  });
              }

              const {
                uploadingAssetFiles,
              } = this.store.getState().modificationRequestEditing.editModificationRequestItem;

              this.store.dispatch(removeUploadingEditFilesProgress(file.id));
              this.store.dispatch(removeUploadingEditAssetFile(file));

              // TODO: キャンセルされたファイルだった場合は反映しないようにしているが本来は belfast に Cancel API があってほしい
              if (uploadingAssetFiles.filter((uploadingFile) => uploadingFile.id === file.id).length > 0) {
                this.store.dispatch(addEditAssetFile(fileReference));
              }
            });
        }
      }
    } catch (err) {
      if (err.name === 'modificationrequestnotdrafterror') {
        this.errorPresenter.showAsNonFatal(new Error('message.modification_request_sent_failed'));
        throw err;
        return;
      }

      this.errorPresenter.showAsNonFatal(err);
      throw err;
    }
  };

  public uploadIssueAssetFiles = async (files: UploadingFile[], bucketId: string, itemId: string): Promise<void> => {
    let uploadingFiles;

    const state = this.store.getState();
    if (state.lastAccessedOrganization == null) {
      return;
    }
    const organizationId = state.lastAccessedOrganization.id;

    try {
      uploadingFiles = validateUploadingFiles(files);

      if (uploadingFiles) {
        this.store.dispatch(addUploadingIssueAssetFiles(uploadingFiles, itemId));

        for (const file of uploadingFiles) {
          await this.belfastClient
            .uploadFile(file, bucketId, organizationId, {
              onUploadProgress: (progressEvent) => {
                const progress = Math.floor((progressEvent.loaded / progressEvent.total) * 100);
                this.store.dispatch(updateUploadingIssueFilesProgress(file.id, progress, itemId));
              },
            })
            .then((fileReference) => {
              this.store.dispatch(removeUploadingIssueFilesProgress(file.id, itemId));
              this.store.dispatch(removeUploadingIssueAssetFile(file, itemId));
              this.store.dispatch(addAssetIssueFile(fileReference, itemId));
            });
        }
      }
    } catch (err) {
      this.errorPresenter.showAsNonFatal(err);
      throw err;
    }
  };

  public removeAssetIssueFile = async (bucketId: string, fileId: string, itemId: string) => {
    this.store.dispatch(removeAssetIssueFile(fileId, itemId));
    await this.belfastClient.deleteFile(fileId, bucketId);
  };

  public uploadIssueCommentAssetFiles = async (
    files: UploadingFile[],
    deliverableId: string,
    issueId: string,
  ): Promise<void> => {
    const modificationRequest = this.store
      .getState()
      .postedModificationRequests.modificationRequests!.find(
        (modificationRequest) => modificationRequest.targetDeliverableId === deliverableId,
      )!;
    const bucketId = modificationRequest.bucketId!;

    const state = this.store.getState();
    if (state.lastAccessedOrganization == null) {
      return;
    }
    const organizationId = state.lastAccessedOrganization.id;

    let uploadingFiles;

    try {
      uploadingFiles = validateUploadingFiles(files);

      if (uploadingFiles) {
        this.store.dispatch(addUploadingIssueCommentAssetFiles(uploadingFiles, issueId));

        for (const file of uploadingFiles) {
          await this.belfastClient
            .uploadFile(file, bucketId, organizationId, {
              onUploadProgress: (progressEvent) => {
                const progress = Math.floor((progressEvent.loaded / progressEvent.total) * 100);
                this.store.dispatch(updateUploadingIssueCommentFilesProgress(file.id, progress, issueId));
              },
            })
            .then((fileReference) => {
              this.store.dispatch(removeUploadingIssueCommentFilesProgress(file.id, issueId));
              this.store.dispatch(removeUploadingIssueCommentAssetFile(file, issueId));
              this.store.dispatch(addAssetIssueCommentFile(fileReference, issueId));
            });
        }
      }
    } catch (err) {
      this.errorPresenter.showAsNonFatal(err);
      throw err;
    }
  };

  public removeAssetIssueCommentFile = async (bucketId: string, fileId: string, issueId: string) => {
    this.store.dispatch(removeAssetIssueCommentFile(fileId, issueId));
    await this.belfastClient.deleteFile(fileId, bucketId);
  };

  public selectedDeliverable = (deliverableId: string): void => {
    this.store.dispatch(setSelectedDeliverable(deliverableId));
  };

  public selectedFeeType = (feeType: ModificationRequestFeeType): void => {
    this.store.dispatch(setSelectedFeeType(feeType));
  };

  public createModificationRequestItem = async (
    modificationRequestId: string,
    bucketId: string | null,
    inputDuration: string,
    inputComment: string,
    files: Array<FileReference>,
  ) => {
    const inputMetadata: ModificationRequestItemMetadata = {
      duration: inputDuration,
    };
    const body: ModificationRequestItemInput = {
      body: inputComment,
      bucketId: bucketId,
      assetIds: files.map((file) => file.id),
      metadata: inputMetadata,
    };

    try {
      const newModificationRequestItem = await this.modificationRequestWebApi.createModificationRequestItem(
        modificationRequestId,
        body,
      );

      this.store.dispatch(addModificationRequestItem(newModificationRequestItem));
    } catch (err) {
      if (err.name === 'modificationrequestnotdrafterror') {
        this.errorPresenter.showAsNonFatal(new Error('message.modification_request_sent_failed'));
        throw err;
        return;
      }

      this.errorPresenter.showAsNonFatal(new Error('message.modification_request_sent_failed'));
    }
  };

  public editModificationRequestItem = async (
    index: number,
    editModificationRequestItem: EditModificationRequestItem,
    modificationRequestItemId: string,
    inputDuration: string,
    inputComment: string,
  ) => {
    if (editModificationRequestItem.item == null) {
      return;
    }
    this.updateModificationRequestItem(index);

    const { bucketId } = this.store.getState().modificationRequestEditing.createModificationRequest;
    const inputMetadata: ModificationRequestItemMetadata = {
      duration: inputDuration,
    };
    const body: ModificationRequestItemInput = {
      body: inputComment,
      bucketId,
      assetIds: editModificationRequestItem.item.assetIds,
      metadata: inputMetadata,
    };

    try {
      const fileIds = editModificationRequestItem.uploadedAssetFileIds.filter(
        (item) => body.assetIds.indexOf(item) == -1,
      );
      if (fileIds.length > 0) {
        fileIds.forEach((fileId) => {
          this.belfastClient.deleteFile(fileId, bucketId);
        });
        await this.modificationRequestWebApi.deleteModificationRequestItemAssets(
          editModificationRequestItem.item.id,
          fileIds,
        );
      }

      const newModificationRequestItem = await this.modificationRequestWebApi.editModificationRequestItem(
        modificationRequestItemId,
        body,
      );

      this.store.dispatch(updateModificationRequestItem(index, newModificationRequestItem));
    } catch (err) {
      if (err.name === 'modificationrequestnotdrafterror') {
        this.errorPresenter.showAsNonFatal(new Error('message.modification_request_sent_failed'));
        throw err;
        return;
      }

      this.errorPresenter.showAsNonFatal(new Error('message.modification_request_sent_failed'));
    }
  };

  public deleteModificationRequestItem = async (modificationRequestItemId: string, index: number) => {
    try {
      const newModificationRequestItems = await this.modificationRequestWebApi
        .deleteModificationRequestItem(modificationRequestItemId)
        .then((modificationRequest) => modificationRequest.items);

      this.removeModificationRequestItem(index);

      this.store.dispatch(setModificationRequestItems(newModificationRequestItems));
    } catch (err) {
      if (err.name === 'modificationrequestnotdrafterror') {
        this.errorPresenter.showAsNonFatal(new Error('message.modification_request_sent_failed'));
        throw err;
        return;
      }

      this.errorPresenter.showAsNonFatal(new Error('message.modification_request_sent_failed'));
    }
  };

  public setEditModificationRequestItem = (index: number) => {
    const modificationRequestItem = this.store.getState().modificationRequestEditing.createModificationRequest.items[
      index
    ];

    if (!modificationRequestItem) {
      throw new Error('modificationRequestItem not found.');
    }

    this.store.dispatch(setEditModificationRequestItem(modificationRequestItem));
  };

  public removeEditAssetFile = async (fileId: string) => {
    this.store.dispatch(removeEditAssetFile(fileId));
  };

  public disposeEditModificationRequestItem = () => {
    const modificationRequestEditing = this.store.getState().modificationRequestEditing;
    const { bucketId } = modificationRequestEditing.createModificationRequest;
    const editModificationRequestItem = modificationRequestEditing.editModificationRequestItem;
    if (editModificationRequestItem.item == null || editModificationRequestItem.uploadedAssetFileIds.length == 0) {
      return;
    }

    this.store.dispatch(disposeEditModificationRequestItem());

    try {
      const fileIds = editModificationRequestItem.uploadedAssetFileIds;
      fileIds.forEach((fileId) => {
        this.belfastClient.deleteFile(fileId, bucketId);
      });
      this.modificationRequestWebApi.deleteModificationRequestItemAssets(editModificationRequestItem.item.id, fileIds);
    } catch (err) {
      if (err.name === 'modificationrequestnotdrafterror') {
        this.errorPresenter.showAsNonFatal(new Error('message.modification_request_sent_failed'));
        throw err;
        return;
      }

      this.errorPresenter.showAsNonFatal(new Error('message.modification_request_sent_failed'));
    }
  };

  private updateModificationRequestItem = (index: number) => {
    const modificationRequestEditing = this.store.getState().modificationRequestEditing;
    const { bucketId } = modificationRequestEditing.createModificationRequest;
    const editModificationRequestItem = modificationRequestEditing.editModificationRequestItem.item!;
    const oldModificationRequestItem = modificationRequestEditing.createModificationRequest.items[index]!;
    // 修正した内容と既存を比較し削除されたファイルはS3からも削除する
    oldModificationRequestItem.assetIds.forEach(async (fileId) => {
      if (!editModificationRequestItem.assetIds.includes(fileId)) {
        await this.belfastClient.deleteFile(fileId, bucketId!);
      }
    });
  };

  private removeModificationRequestItem = (key: number) => {
    const { items } = this.store.getState().modificationRequestEditing.createModificationRequest;

    if (items.length > 0) {
      const { bucketId, assetIds } = items[key]!;
      const newItems = items.filter((_, index) => index != key);
      this.store.dispatch(setModificationRequestItems(newItems));

      if (assetIds.length > 0) {
        assetIds.forEach(async (fileId) => await this.belfastClient.deleteFile(fileId, bucketId!));
      }
    }
  };

  public submitModificationRequest = async (): Promise<void> => {
    this.store.dispatch(incrementFullScreenSpinnerNecessity());

    const state = this.store.getState();
    const createModificationRequest = state.modificationRequestEditing.createModificationRequest;

    try {
      await this.modificationRequestWebApi.submitModificationRequest(
        createModificationRequest.id,
        createModificationRequest.feeType,
      );

      const updatedCreation = await this.creationWebApi.getByIdWithoutPricingData(state.creation.creation!.id);
      this.store.dispatch(setCreation(updatedCreation));

      if (updatedCreation.organization) {
        this.store.dispatch(setLastAccessedOrganization(updatedCreation.organization));
      }

      this.messageDispatcher.notifyUnimportant('message.modification_request_sent');
    } catch (err) {
      if (err.name === 'ExpiredModificationRequestError') {
        this.errorPresenter.showAsNonFatal(
          new Error('modification_request_creating_card.expired_at_message_for_snackbar'),
        );
        throw err;
        return;
      }

      if (err instanceof AlreadyModificationRequestedError) {
        this.errorPresenter.showAsNonFatal(new Error('message.already_modification_requested'));
        throw err;
        return;
      }

      if (err.type === 'PointShortage') {
        this.errorPresenter.showAsNonFatal(new Error('message.point_shortage_for_modification_request'));
        throw err;
        return;
      }

      this.errorPresenter.showAsNonFatal(new Error('message.modification_request_sent_failed'));
      throw err;
    } finally {
      this.store.dispatch(decrementFullScreenSpinnerNecessity());
    }
  };

  public createModificationRequestItemIssue = async (
    modificationRequestItemId: string,
    body: string,
    bucketId: string | null,
    assetIds: Array<string>,
  ): Promise<void> => {
    const state = this.store.getState();
    const modificationRequests = state.postedModificationRequests.modificationRequests;
    const createIssues = state.modificationRequestEditing.createIssues;

    try {
      const modificationRequestIssue = await this.modificationRequestWebApi.createModificationRequestItemIssue(
        modificationRequestItemId,
        'Untitled',
        body,
        bucketId,
        assetIds,
      );

      modificationRequests!.map((modificationRequest, index) =>
        modificationRequest.items.map((item, itemIndex) => {
          if (item.id === modificationRequestItemId) {
            const modificationRequestIndex = index;
            const modificationRequestItemIndex = itemIndex;

            this.store.dispatch(
              setModificationRequestIssue(
                modificationRequestIndex,
                modificationRequestItemIndex,
                modificationRequestIssue,
              ),
            );
          }
        }),
      );

      createIssues.map((issue, index) => {
        if (issue.itemId === modificationRequestItemId) {
          this.store.dispatch(resetUploadingIssueAssetFile(index));
        }
      });

      const createIssueComments: Array<CreateIssueComment> = [];
      createIssueComments.push({
        issueId: modificationRequestIssue.id,
        assetIds: [],
        uploadingAssetFiles: [],
        uploadingFilesProgress: {},
      });

      this.store.dispatch(setCreateIssueComment(createIssueComments));

      this.messageDispatcher.notifyUnimportant('message.modification_request_issue_sent');
    } catch (err) {
      this.errorPresenter.showAsNonFatal(new Error('message.modification_request_issue_sent_failed'));
    }
  };

  public createModificationRequestItemIssueComment = async (id: string, comment: string): Promise<void> => {
    const state = this.store.getState();
    const modificationRequests = state.postedModificationRequests.modificationRequests;
    const createIssueComments = state.modificationRequestEditing.createIssueComments;

    const bucketId = modificationRequests!.find((modificationRequest) => {
      const item = modificationRequest.items.find((item) => (item.issue ? item.issue.id === id : false));
      return !!item;
    })?.bucketId;

    const issueComment = createIssueComments.find((comment) => comment.issueId === id);
    const assetIds = issueComment ? issueComment.assetIds : [];

    try {
      const modificationRequestIssueComment = await this.modificationRequestWebApi.createModificationRequestIssueComment(
        id,
        comment,
        bucketId ?? null,
        assetIds,
      );

      modificationRequests!.map((modificationRequest, index) =>
        modificationRequest.items.map((item, itemIndex) => {
          if (item.issue && item.issue.id === id) {
            const modificationRequestIndex = index;
            const modificationRequestItemIndex = itemIndex;

            this.store.dispatch(
              setModificationRequestIssueComment(
                modificationRequestIndex,
                modificationRequestItemIndex,
                modificationRequestIssueComment,
              ),
            );
          }
        }),
      );

      createIssueComments.map((comment, index) => {
        if (comment.issueId === id) {
          this.store.dispatch(resetUploadingIssueCommentAssetFile(index));
        }
      });

      this.messageDispatcher.notifyUnimportant('message.modification_request_issue_comment_sent');
    } catch (err) {
      this.errorPresenter.showAsNonFatal(new Error('message.modification_request_issue_comment_sent_failed'));
      throw err;
    }
  };

  public resolveModificationRequestIssue = async (modificationRequestIssueId: string): Promise<void> => {
    const modificationRequests = this.store.getState().postedModificationRequests.modificationRequests;

    if (!modificationRequests || modificationRequests.length === 0) {
      throw new Error('modificationRequests.size === 0');
    }

    try {
      await this.modificationRequestWebApi.resolveModificationRequestIssue(modificationRequestIssueId);

      modificationRequests.map((modificationRequest, index) =>
        modificationRequest.items.map((item, itemIndex) => {
          if (item.issue && item.issue.id === modificationRequestIssueId) {
            const modificationRequestIndex = index;
            const modificationRequestItemIndex = itemIndex;

            this.store.dispatch(
              setResolveModificationRequestIssue(modificationRequestIndex, modificationRequestItemIndex),
            );
          }
        }),
      );

      this.messageDispatcher.notifyUnimportant('message.modification_request_issue.change_state.closed');
    } catch (err) {
      this.errorPresenter.showAsNonFatal(new Error('message.modification_request_issue_change_state.failed'));
    }
  };

  public reopenModificationRequestIssue = async (modificationRequestIssueId: string): Promise<void> => {
    const modificationRequests = this.store.getState().postedModificationRequests.modificationRequests;

    if (!modificationRequests || modificationRequests.length === 0) {
      throw new Error('modificationRequests.size === 0');
    }

    try {
      const modificationRequestIssue = await this.modificationRequestWebApi.reopenModificationRequestIssue(
        modificationRequestIssueId,
      );

      modificationRequests.map((modificationRequest, index) =>
        modificationRequest.items.map((item, itemIndex) => {
          if (item.issue && item.issue.id === modificationRequestIssueId) {
            const modificationRequestIndex = index;
            const modificationRequestItemIndex = itemIndex;

            this.store.dispatch(
              setReopenModificationRequestIssue(
                modificationRequestIndex,
                modificationRequestItemIndex,
                modificationRequestIssue,
              ),
            );
          }
        }),
      );

      this.messageDispatcher.notifyUnimportant('message.modification_request_issue.change_state.open');
    } catch (err) {
      this.errorPresenter.showAsNonFatal(new Error('message.modification_request_issue_change_state.failed'));
    }
  };

  public approveModificationRequest = async (modificationRequestId: string): Promise<void> => {
    this.store.dispatch(incrementFullScreenSpinnerNecessity());

    const state = this.store.getState();
    const modificationRequests = state.postedModificationRequests.modificationRequests;

    try {
      const modificationRequest = await this.modificationRequestWebApi.approveModificationRequest(
        modificationRequestId,
      );

      modificationRequests!.map((item, index) => {
        if (item.id === modificationRequestId) {
          this.store.dispatch(setModificationRequest(index, modificationRequest));
        }
      });

      this.messageDispatcher.notifyUnimportant('message.modification_request_approved');
    } catch (err) {
      if (err instanceof AlreadyApprovedModificationRequestError) {
        this.errorPresenter.showAsNonFatal(new Error('message.modification_request_already_approved'));
      } else if (err instanceof ExpiredModificationRequestError) {
        this.errorPresenter.showAsNonFatal(new Error('message.modification_request_approve_failed'));
        throw err;
      }
    } finally {
      this.store.dispatch(decrementFullScreenSpinnerNecessity());
    }
  };

  public dispose = (): void => {
    this.store.dispatch(dispose());
  };
}

export default ModificationRequestUsecase;
